/**
 * Owner history pagination helpers (newest page + explicit older load).
 * Stale-response guard mirrors React ignore-flag pattern for async races.
 */

/** @returns {boolean} */
export function shouldAcceptHistoryResponse(requestId, activeRequestId) {
  return requestId === activeRequestId;
}

/** Map owner turn → GatewayChat message shape (no custom category labels). */
export function turnToMessage(d) {
  const cats = Array.isArray(d.categories) ? d.categories : [];
  return {
    id: d.id,
    prompt: d.prompt_full || "",
    verified: d.verification_status === "verified",
    verified_by: d.verified_by,
    verified_at: d.verified_at,
    verification_status: d.verification_status,
    remediation: null,
    steps: {
      attachments: d.attachments || [],
      detection: {
        clean: !cats.length,
        categories: cats.map((id) => ({ id, label: id })),
        entities: [],
        llm_used: false,
      },
      policy: { decisions: [], overall_applied: d.decision },
      routing: { reason: "", us_excluded: false, chosen: null },
      response: {
        text: "",
        final: d.response_final != null ? d.response_final : null,
        content_status: d.content_status || (d.response_final != null ? "ok" : "final_unavailable"),
        blocked: d.decision === "BLOCK",
        held: !!d.held,
        masked_count: d.masked_count || 0,
        masked_prompt: "",
        latency_ms: 0,
        simulated: false,
      },
    },
  };
}

/** Apply newest page payload into UI state fields. */
export function applyNewestHistoryPage(page) {
  const turns = page?.turns || [];
  const hasMore = !!page?.has_more;
  return {
    messages: turns.map(turnToMessage),
    hasMore,
    cursor: hasMore
      ? { before_ts: page.next_before_ts, before_id: page.next_before_id }
      : null,
  };
}

/** Prepend older page turns; refresh cursor from that page. */
export function mergeOlderHistoryPage(existingMessages, page) {
  const older = (page?.turns || []).map(turnToMessage);
  const hasMore = !!page?.has_more;
  return {
    messages: [...older, ...(existingMessages || [])],
    hasMore,
    cursor: hasMore
      ? { before_ts: page.next_before_ts, before_id: page.next_before_id }
      : null,
  };
}

/**
 * Continue navigates with this history state. The persistent restore key
 * (`lg_active_conv_*`) is written only after owner turns load successfully.
 */
export function chatNavigateOptions(conversationId) {
  if (typeof conversationId !== "string" || !conversationId) return {};
  return { state: { openConversationId: conversationId } };
}

/** Conversation id carried on the Chat location, if Continue just set one. */
export function conversationIdFromLocationState(state) {
  const id = state && typeof state === "object" ? state.openConversationId : "";
  return typeof id === "string" && id ? id : "";
}

/**
 * Continue id applies once. consumeLocationState tells Chat to replace the
 * history entry so a refresh uses the restore key, not this Continue id.
 */
export function conversationToOpenOnMount(openConversationId, storedId) {
  if (openConversationId) {
    return { conversationId: openConversationId, consumeLocationState: true };
  }
  const stored = typeof storedId === "string" ? storedId : "";
  return { conversationId: stored, consumeLocationState: false };
}

/** Clear the restore pointer only when it is the id that failed to load. */
export function shouldClearStoredConversation(storedId, failedId) {
  return Boolean(failedId) && storedId === failedId;
}

/** Empty the visible chat only when the failed id is the one already on screen. That path must resetHistory() so the older-page cursor does not stick. */
export function shouldResetChatAfterFailedLoad(currentConversationId, failedId) {
  return Boolean(failedId) && currentConversationId === failedId;
}

/**
 * Banner text after an active (non-stale) conversation open settles.
 * Success clears a prior load failure; stale responses leave the previous banner.
 */
export function nextConversationLoadError(previousError, { accepted, ok }) {
  if (!accepted) return previousError || "";
  return ok ? "" : "Could not load conversation";
}

/** Fresh id for empty chat / 404 reset (no prefill). */
export function newConversationId() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `c_${Date.now()}`;
}

/** Drawer chip: catalogue trusted_label, else category id. Never free-text label. */
export function entityChipLabel(entity) {
  if (!entity || typeof entity !== "object") return "";
  return entity.trusted_label || entity.category || "";
}

/** Category chips for list/export rows (ids or trusted fixed labels only). */
export function listCategoryChips(row) {
  const cats = Array.isArray(row?.categories) ? row.categories : [];
  if (!cats.length) return [];
  const trusted = {};
  for (const d of row?.decisions || []) {
    if (d?.category && d.trusted_label) trusted[d.category] = d.trusted_label;
  }
  for (const e of row?.detected_entity_meta || []) {
    if (e?.category && e.trusted_label) trusted[e.category] = e.trusted_label;
  }
  return cats.map((id) => trusted[id] || id);
}
